<template>
  <div class="filter-card demographic-filter-card">
    <div class="filter-card-header">
      <div class="filter-card-title">
        <i class="fa fa-user filter-icon"></i>
        <span>Patient Demographic Filter</span>
      </div>
      <b-dropdown variant="link" size="sm" no-caret right class="filter-menu">
        <template #button-content>
          <i class="fa fa-ellipsis-v text-muted"></i>
        </template>
        <b-dropdown-item @click="$emit('duplicate')">
          <i class="fa fa-copy mr-2"></i>Duplicate
        </b-dropdown-item>
        <b-dropdown-item variant="danger" @click="$emit('remove')">
          <i class="fa fa-trash mr-2"></i>Remove
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <div class="filter-card-body">
      <b-row>
        <!-- Age Range -->
        <b-col md="4">
          <div class="mb-3">
            <label class="filter-label">Age Range</label>
            <div class="age-range-row">
              <input
                v-model.number="localFilter.ageMin"
                type="number"
                min="0"
                max="120"
                placeholder="Min"
                class="filter-form-input age-input"
                @input="emitUpdate"
              />
              <span class="age-separator">to</span>
              <input
                v-model.number="localFilter.ageMax"
                type="number"
                min="0"
                max="120"
                placeholder="Max"
                class="filter-form-input age-input"
                @input="emitUpdate"
              />
            </div>
            <p class="filter-form-help">Max is optional</p>
          </div>
        </b-col>

        <!-- Gender -->
        <b-col md="4">
          <div class="mb-3">
            <label class="filter-label">Gender</label>
            <div class="filter-radio-group filter-radio-stacked">
              <label class="filter-radio-option">
                <input type="radio" value="all" v-model="localFilter.gender" @change="emitUpdate" />
                <span>All</span>
              </label>
              <label class="filter-radio-option">
                <input type="radio" value="male" v-model="localFilter.gender" @change="emitUpdate" />
                <span>Male Only</span>
              </label>
              <label class="filter-radio-option">
                <input type="radio" value="female" v-model="localFilter.gender" @change="emitUpdate" />
                <span>Female Only</span>
              </label>
              <label class="filter-radio-option">
                <input type="radio" value="unknown" v-model="localFilter.gender" @change="emitUpdate" />
                <span>Unknown</span>
              </label>
            </div>
          </div>
        </b-col>

        <!-- Zip Codes -->
        <b-col md="4">
          <div class="mb-3">
            <label class="filter-label">Zip Codes</label>
            <multiselect
              v-model="selectedZipCodes"
              :options="zipCodeOptions"
              :multiple="true"
              :searchable="true"
              :close-on-select="false"
              :show-labels="false"
              label="text"
              track-by="value"
              placeholder="Search zip codes..."
              class="filter-multiselect"
              @input="onZipCodesChange"
            />
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: 'DemographicFilterCard',
  components: { Multiselect },
  props: {
    value: {
      type: Object,
      required: true
    },
    zipCodeOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localFilter: JSON.parse(JSON.stringify(this.value)),
      selectedZipCodes: []
    };
  },
  watch: {
    value: {
      handler(newVal) {
        this.localFilter = JSON.parse(JSON.stringify(newVal));
        this.syncSelections();
      },
      deep: true
    }
  },
  created() {
    this.syncSelections();
  },
  methods: {
    syncSelections() {
      this.selectedZipCodes = (this.localFilter.zipCodes || [])
        .map(zip => this.zipCodeOptions.find(o => o.value === zip))
        .filter(Boolean);
    },
    onZipCodesChange(selected) {
      this.localFilter.zipCodes = selected.map(s => s.value);
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('input', { ...this.localFilter });
    }
  }
};
</script>

<style scoped lang="scss">
.demographic-filter-card {
  border-left: 3px solid #10b981;
}

.filter-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: visible;
}

.filter-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0.875rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.filter-card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #334155;

  .filter-icon {
    color: #10b981;
    font-size: 0.875rem;
  }
}

.filter-menu ::v-deep .btn-link {
  padding: 0.125rem 0.375rem;
  color: #94a3b8;
  &:hover { color: #475569; }
}

.filter-card-body {
  padding: 0.875rem;
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 0.375rem;
  display: block;
}

// Native form input styling (matches Online Scheduling pattern)
.filter-form-input {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.8125rem;
  outline: none;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    border-color: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.15);
  }
}

.filter-form-help {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 4px;
  margin-bottom: 0;
}

.filter-radio-group {
  display: flex;
  gap: 16px;
}

.filter-radio-stacked {
  flex-direction: column;
  gap: 6px;
}

.filter-radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 0;

  input[type="radio"] {
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
    accent-color: #10b981;
  }
}

.age-range-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.age-input {
  width: 80px;
}

.age-separator {
  font-size: 0.8125rem;
  color: #64748b;
}

.filter-multiselect ::v-deep {
  .multiselect__tags {
    min-height: 34px;
    padding: 4px 40px 0 8px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 0.8125rem;
  }
  .multiselect__tag {
    background: #d1fae5;
    color: #065f46;
    border-radius: 4px;
    font-size: 0.75rem;
    padding: 2px 22px 2px 8px;
    margin-bottom: 2px;
  }
  .multiselect__tag-icon {
    &:after { color: #10b981; }
    &:hover { background: #a7f3d0; }
  }
  .multiselect__option--highlight {
    background: #10b981;
  }
  .multiselect__content-wrapper {
    z-index: 1060;
    border-radius: 6px;
    border-color: #e2e8f0;
    max-height: 200px;
  }
  .multiselect__placeholder {
    font-size: 0.8125rem;
    color: #94a3b8;
    padding-top: 0;
    margin-bottom: 0;
  }
}
</style>
